import { MouseEvent } from "react";

import { Box, Grid } from "@mui/material";

import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardInformationTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardInformationTooltip";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { IconElement } from "@/icons/types";

export type Tab<T> = {
  label: string;
  value: T;
  icon?: IconElement;
  notification?: number;
  tooltip?: string;
};

export interface ArchivedActiveSelectProps<T> {
  currentTab?: T;
  onChange: (value: T) => void;
  tabs: Tab<T>[];
  backgroundColor?: string;
  fullWidthButtons?: boolean;
  padding?: number;
}

export const TabMenu = <T,>({
  currentTab,
  onChange,
  tabs,
  backgroundColor,
  fullWidthButtons,
  padding = 1,
}: ArchivedActiveSelectProps<T>) => {
  const { isPhoneScreen, isMediumScreen } = useMediaQuery();

  const handleClick = (_event: MouseEvent<HTMLButtonElement>, value: T) => {
    onChange(value);
  };

  const displayIcon = isPhoneScreen && tabs.length > 2;

  return (
    <Box
      sx={(theme) => ({
        width: "100%",
        backgroundColor: backgroundColor ?? theme.palette.surface.neutral.supershy,
        display: "flex",
        alignItems: "center",
        padding,
        borderRadius: 1,
        boxSizing: "border-box",
      })}
      data-test="tab-menu"
    >
      {tabs.map((tab, index) => {
        const isSelected = currentTab === tab.value;
        if (displayIcon && tab.icon) {
          return (
            <Grid
              item
              marginRight={index !== tabs.length - 1 ? 1 : 0}
              key={String(tab.value)}
              flex={isSelected ? 1 : undefined}
              data-test="tab-menu-button"
            >
              <DashboardButton
                onClick={(event) => handleClick(event, tab.value)}
                role="tab"
                aria-selected={isSelected}
                startIcon={isSelected ? undefined : tab.icon}
                vibe={isSelected ? "brand" : "neutral"}
                variant="text"
                sx={{
                  paddingInline: 1.5,
                  backgroundColor: isSelected ? "white" : "transparent",
                  ...(!isSelected && {
                    gap: 0,
                  }),
                }}
                fullWidth
              >
                {isSelected ? tab.label : ""}
              </DashboardButton>
            </Grid>
          );
        }

        return (
          <DashboardButton
            key={String(tab.value)}
            onClick={(event) => handleClick(event, tab.value)}
            role="tab"
            aria-selected={isSelected}
            startIcon={tab.icon}
            vibe={isSelected ? "brand" : "neutral"}
            data-test="tab-menu-button"
            variant="text"
            sx={{
              marginRight: index !== tabs.length - 1 ? 1 : 0,
              flex: isMediumScreen ? 1 : undefined,
              background: isSelected ? "white" : "transparent",
              ...(!isSelected && {
                color: (theme) => theme.palette.text.neutral.quiet,
                ["&:hover"]: {
                  color: (theme) => theme.palette.text.neutral.moderate,
                },
              }),
              ...(fullWidthButtons && {
                flex: 1,
              }),
            }}
          >
            <span>
              {tab.label}
              {tab.tooltip && (
                <DashboardInformationTooltip
                  sx={{
                    marginLeft: 0.5,
                  }}
                  slotProps={{
                    icon: {
                      vibe: isSelected ? "primary" : "inherit",
                    },
                  }}
                  title={tab.tooltip}
                  placement="bottom"
                  alignOnText
                  alignOffset="0.15rem"
                />
              )}
            </span>
            {tab.notification && tab.notification > 0 ? (
              <Box
                sx={{
                  position: "absolute",
                  top: -4,
                  right: -4,
                  backgroundColor: (theme) => theme.palette.text.positive.quiet,
                  borderRadius: "50%",
                  width: 19,
                  height: 19,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DashboardTypography variant="caption" vibe="neutral-100">
                  {tab.notification}
                </DashboardTypography>
              </Box>
            ) : null}
          </DashboardButton>
        );
      })}
    </Box>
  );
};
